import defaultMiniature from "assets/images/curved-images/digitization.png";
import React, {useEffect, useState} from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {Box, Stack, Tooltip} from '@mui/material';
import Typography from "@mui/material/Typography";
import { format } from 'date-fns';
import { SquareArrowUpRight } from "lucide-react";
import {io} from 'socket.io-client';
import StepperComponent from "../../Steper/StepperComponent";

const CardItem = ({
    id, title, author, publishedAt, createdAt, citationsCount, referencesCount, fetchArticles, status
}) => {

    const openReferences = (id) => {
        window.open(`/article/${id}`, "_blank");
    }

    const [stepsData, setStepsData] = useState({steps_data: ""});

    useEffect(() => {

        if (status === "IN_PROGRESS") {
            const baseURL = window?.appConfig?.REACT_APP_SOCKET_SERVER || process.env.REACT_APP_SOCKET_SERVER
            const socket = io(baseURL, {
                path: "/socket.io"
            });
            const eventName = `indexing_status_update-${id}`;
            socket.on(eventName, (res) => {

                setStepsData(res)
                if (res.status === 'COMPLETED') {
                    socket.off(`indexing_status_update-${id}`);
                    fetchArticles()
                } else if (res.status === 'ERROR') {
                    fetchArticles()
                }
            });

            return () => {
                socket.off(`indexing_status_update-${id}`);
            };
        }
    }, []);

    const formattedCreatedAt = createdAt ? format(new Date(createdAt), 'MMM dd, yyyy hh:mm a') : '';

    return(
        <Card
            sx={{
                position: "relative",
                overflow: "visible",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: 1.5,
                boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.25)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    background: "rgba(255, 255, 255, 0.15)",
                    boxShadow: "0 25px 50px -12px rgb(0 0 0 / 0.35)",
                    "& .desktop-action-buttons": {
                        opacity: 1,
                    },
                },
            }}
        >
                        {/* Header Section with glass effect */}
            <Box
                sx={{
                    position: "relative",
                    background: "rgba(241, 245, 249, 0.8)",
                    backdropFilter: "blur(20px)",
                    borderRadius: 1.5,
                    borderBottom: "1px solid rgb(241 245 249)",
                    padding: "12px 24px",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            sx={{
                                width: { xs: 40, sm: 64 },
                                height: { xs: 58, sm: 82 },
                                borderRadius: 1.5,
                                background: "rgba(255, 255, 255, 0.3)",
                                backdropFilter: "blur(4px)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0.5,
                                position: "relative",
                            }}
                        >
                            <Box
                                component="img"
                                src={defaultMiniature}
                                alt={"Document"}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: 0.5,
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: "grey.900",
                                    mb: 0.5,
                                    fontSize: "1.25rem",
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}
                            >
                                {title}
                                <Box sx={{ display: "flex", alignItems: "center", ml: 1, gap: 1 }}>
                                
                                {/* @TODO UNCOMMENT WHEN THE EDIT FEATURE IS IMPLEMENTED */} 
                                {/* {controller.profileData.id === digitizerId && (
                                    <Tooltip title={t("edit")}>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                ml: 1,
                                                color: "grey.700",
                                                padding: 0.5,
                                                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                                            }}
                                            onClick={handleOpenMetadataDialog}
                                        >
                                            <Edit sx={{ fontSize: "16px" }} />
                                        </IconButton>
                                    </Tooltip>
                                )} */}
                                </Box>
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                📅
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "grey.600",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                    }}
                                >
                                    {formattedCreatedAt}
                                    
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        className="desktop-action-buttons"
                        sx={{
                            display: "flex",
                            gap: 1,
                        }}
                    >
                        <Box
                            className="desktop-action-buttons"
                            sx={{
                                display: "grid",
                                gap: 1,
                            }}
                        >
                    
                            {referencesCount > 0 &&
                            <Typography
                                variant="link"
                                sx={{
                                    fontWeight: 600,
                                    color: "grey.900",
                                    fontSize: "0.875rem",
                                }}
                            >
                                References: {referencesCount}
                            </Typography>
                            }
                            {citationsCount > 0 &&
                                <Typography
                                variant="link"
                                sx={{
                                    fontWeight: 600,
                                    color: "grey.900",
                                    fontSize: "0.875rem",
                                }}
                            >
                                Citations: {citationsCount}
                            </Typography>
                            }
                        </Box>    

                        {(referencesCount > 0 || citationsCount > 0) && (
                            <Tooltip title="Open references & citations">
                            <Box
                                component="a"
                                sx={{
                                    cursor: "pointer",
                                    textDecoration: "none",
                                    color: "inherit",
                                }}
                                onClick={() => openReferences(id)}
                                >
                                <SquareArrowUpRight size={16} />
                            </Box>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </Box>

           {/* Main Content Section */}
           <Box sx={{ margin: '5px', display: 'flex', justifyContent: 'center', width: '100%'}}>
                {status == "IN_PROGRESS" && 
                    <StepperComponent stepsData={stepsData.steps_data} />
                }
           </Box>
            <CardContent
                sx={{
                    background: "rgba(255, 255, 255, 0.05)",
                    backdropFilter: "blur(8px)",
                    padding: "16px 24px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        gap: { xs: 3, lg: 4 },
                    }}
                >
                    {/* Left column - Document details */}
                    <Box sx={{ flex: "2", minWidth: 0 }}>
                        <Stack spacing={2}>
                            {/* Author and Published Date */}
                            <Stack spacing={2.5}>
                                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "grey.700",
                                            minWidth: 80,
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        Author:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "grey.900",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {author}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "grey.700",
                                            minWidth: 80,
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        Published:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            color: "grey.900",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                     {publishedAt}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </CardContent>
                        {/* Footer Section */}
            <Box
                sx={{
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(20px)",
                    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                    p: 3,
                }}
            >
                
            </Box>
        </Card>

    );
}
export default CardItem;